import { Icon, Snackbar, Theme, useTheme } from "@outschool/backpack";
import { faTimes } from "@outschool/icons";
import React, { createContext, useContext, useState } from "react";

type SnackbarContextType = {
  addSnackbarContent: (
    content: React.ReactNode,
    durationMs?: number,
    backgroundColor?: string,
  ) => void;
  handleSnackbarClose?: (event: React.SyntheticEvent | Event) => void;
};

export const SnackbarContext = createContext<SnackbarContextType>({
  addSnackbarContent: () => {},
  handleSnackbarClose: () => {},
});

export const SnackbarProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const theme = useTheme();

  const [snackbarData, setSnackbarData] = useState<{
    isOpen: boolean;
    content?: React.ReactNode;
    durationMs: number;
    backgroundColor?: string;
  }>({
    isOpen: false,
    content: undefined,
    durationMs: 0,
    backgroundColor: "success",
  });

  const addSnackbarContent = (
    content: React.ReactNode,
    durationMs: number = 60000,
    backgroundColor: string = "success",
  ) => {
    setSnackbarData({
      isOpen: true,
      content,
      durationMs,
      backgroundColor:
        backgroundColor === "success"
          ? theme.palette.success.dark
          : backgroundColor === "error"
          ? theme.palette.error.main
          : backgroundColor,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarData({
      isOpen: false,
      content: undefined,
      durationMs: 0,
      // Preserve the last background colour when closing the Snackbar, otherwise it will flash to the default color while closing
      backgroundColor: snackbarData.backgroundColor,
    });
  };

  return (
    <SnackbarContext.Provider
      value={{ addSnackbarContent, handleSnackbarClose }}
    >
      {children}
      <Snackbar
        open={snackbarData.isOpen}
        autoHideDuration={snackbarData.durationMs}
        onClose={handleSnackbarClose}
        message={snackbarData.content}
        action={
          <Icon
            onClick={handleSnackbarClose}
            icon={faTimes}
            size="medium"
            sx={{ cursor: "pointer" }}
          />
        }
        sx={(theme: Theme) => ({
          [theme.breakpoints.down("md")]: {
            // On mobile, move the Snackbar up to avoid covering mobile footer, and add horizontal margin to reduce width
            "&.MuiSnackbar-root": { bottom: "72px", margin: "0 16px" },
          },
          "& .MuiSnackbarContent-root": {
            color: "white",
            backgroundColor: snackbarData.backgroundColor,
          },
        })}
      />
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => useContext(SnackbarContext);
